import type { CheckboxTreeOptionType } from 'platform/components';
import type { EagColumn } from '../../../../../types/EagColumn';
import type { EagColumnOrGroup } from '../../../../../types/EagColumnGroup';
import type { EagColumnState } from '../../../../../types/EagColumnState';
import { isEagColumnGroup } from '../../../../../utils/isEagColumnGroup';

/**
 * Filters CheckboxTree options by a lowercase search term.
 *
 * For leaf nodes: included if label contains the search term.
 * For group nodes:
 *  - If group label matches → include group with ALL its children.
 *  - If group label doesn't match → include group with only matching children.
 *  - If no match at all → exclude the group.
 *
 * Returns a new array (no mutation of input).
 */
export const filterCheckboxTreeOptions = (options: CheckboxTreeOptionType[], searchTerm: string): CheckboxTreeOptionType[] => {
  const lower = searchTerm.toLowerCase();
  if (!lower) {
    return options;
  }
  const result: CheckboxTreeOptionType[] = [];
  for (const option of options) {
    if (!option.options?.length) {
      // Leaf node
      if (option.label.toLowerCase().includes(lower)) {
        result.push(option);
      }
      continue;
    }

    // Group node
    const groupLabelMatches = option.label.toLowerCase().includes(lower);
    if (groupLabelMatches) {
      // Keep group with all children
      result.push(option);
      continue;
    }
    const matchingChildren = option.options.filter(child => child.label.toLowerCase().includes(lower));
    if (matchingChildren.length > 0) {
      result.push({
        ...option,
        options: matchingChildren
      });
    }
  }
  return result;
};

/**
 * Filters flat columns by headerName using a lowercase search term.
 */
export const filterFlatColumns = (columns: EagColumn[], searchTerm: string): EagColumn[] => {
  const lower = searchTerm.toLowerCase();
  if (!lower) {
    return columns;
  }
  return columns.filter(column => column.headerName.toLowerCase().includes(lower));
};

/**
 * Returns the set of column keys that match the current search term.
 *
 * When searchTerm is empty, returns all keys from columnStates.
 * When columnConfig has groups, traverses groups to find matching leaf keys.
 * When no groups, filters flat columns by headerName.
 */
export const getMatchingColumnKeys = (columnConfig: EagColumnOrGroup[] | undefined, columns: EagColumn[], columnStates: EagColumnState[], searchTerm: string): Set<string> => {
  const lower = searchTerm.toLowerCase();
  if (!lower) {
    return new Set(columnStates.map(cs => cs.key));
  }
  const hasGroups = columnConfig && columnConfig.some(isEagColumnGroup);
  if (hasGroups && columnConfig) {
    const matchingKeys = new Set<string>();
    for (const item of columnConfig) {
      if (isEagColumnGroup(item)) {
        const groupLabelMatches = item.headerName.toLowerCase().includes(lower);
        if (groupLabelMatches) {
          for (const child of item.children) {
            matchingKeys.add(child.key);
          }
        } else {
          for (const child of item.children) {
            if (child.headerName.toLowerCase().includes(lower)) {
              matchingKeys.add(child.key);
            }
          }
        }
      } else {
        if (item.headerName.toLowerCase().includes(lower)) {
          matchingKeys.add(item.key);
        }
      }
    }
    return matchingKeys;
  }
  const matchingKeys = new Set<string>();
  for (const column of columns) {
    if (column.headerName.toLowerCase().includes(lower)) {
      matchingKeys.add(column.key);
    }
  }
  return matchingKeys;
};